import { ShowToasterService } from 'src/app/bar/services/show-toaster.service';
import tinymce, { Editor } from 'tinymce';
import * as uuid from 'uuid';
import { getSizeFileError, showEditorToasterError, validateFileSize, frameEditorNumbers } from '../constants/frame-editor-utils';
import { BlobsService } from 'src/app/bar/services/blobs/blob.service';
import { Injectable } from '@angular/core';
import { FrameEditorBlobService } from './frame-editor-blob.service';
import { BlobsStorageService } from 'src/app/bar/services/blobs/blobs-storage.service';
import { Wiki } from '@local/client-contracts';

export type SupportFileType = 'image' | 'video';

@Injectable()
export class FrameEditorMediaService {
  private readonly ACCEPT_MEDIA_UPLOAD_FILE_TYPES: { [name: string]: string[] } = { image: ['image'], video: ['video', 'audio'] };

  private readonly MAX_MEDIA_FILE_SIZE_MB = 10;
  private readonly CLASS_TOX_DIALOG = '.tox-dialog';
  private readonly CLASS_ERROR_BORDER = 'error-border';
  private readonly CLASS_TOX_FORM_GROUP = '.tox-form__group';
  private readonly CLASS_ERROR_MESSAGE = 'error-message';

  constructor(
    private showToasterService: ShowToasterService,
    private blobsService: BlobsService,
    private frameEditorStorageAttachmentsService: BlobsStorageService<Wiki.CardAttachment>,
    private frameEditorBlobService: FrameEditorBlobService
  ) {}

  addImageOrVideoToEditor(callback, meta, followingId: string, editorId: string) {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    const accept = meta.filetype === 'image' ? 'image/*' : 'video/*, audio/*';
    input.setAttribute('accept', accept);
    input.onchange = () => {
      this.handleMedia(input, followingId, editorId, null, callback);
    };
    input.click();
  }

  private handleMedia(input, followingId: string, editorId: string, editor?: Editor, callback?, fileType?: SupportFileType) {
    const file: File = input.files[0];
    const maxSize = this.MAX_MEDIA_FILE_SIZE_MB * frameEditorNumbers.MB;
    if (file.size > maxSize) {
      return this.showErrorInput(maxSize);
    }
    this.removeErrorInput();
    this.handleAddedMedia(file, followingId, editorId, editor, callback, fileType);
  }

  uploadImage(followingId: string, editorId: string) {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.onchange = () => {
      this.handleMedia(input, followingId, editorId, tinymce.get(editorId), null, 'image');
    };
    input.click();
  }

  private showErrorInput(maxSize: number) {
    const dialog = document.querySelector(this.CLASS_TOX_DIALOG);
    if (!dialog) {
      return showEditorToasterError(this.showToasterService, getSizeFileError(maxSize));
    }
    const firstInput = dialog.querySelector('input');
    if (firstInput.className.includes(this.CLASS_ERROR_BORDER)) {
      return;
    }
    if (!firstInput) return;
    const firstGroup = dialog.querySelector(this.CLASS_TOX_FORM_GROUP);
    firstInput.classList.add(this.CLASS_ERROR_BORDER);
    const errorMessageElement = document.createElement('div');
    errorMessageElement.innerText = getSizeFileError(maxSize);
    errorMessageElement.classList.add(this.CLASS_ERROR_MESSAGE);
    firstGroup.appendChild(errorMessageElement);
  }

  private removeErrorInput() {
    const dialog = document.querySelector('.tox-dialog');
    if (!dialog) {
      return;
    }
    const firstInput = dialog.querySelector('input');
    const errorMessage = dialog.querySelector('.error-message');
    if (firstInput) {
      firstInput.classList.remove('error-border');
      errorMessage?.remove();
    }
  }

  handleAddedMedia(file, followingId: string, editorId: string, editor?: Editor, callback?, fileType?: SupportFileType) {
    const reader = new FileReader();
    try {
      reader.onload = async () => {
        const tempId = 'blobid-' + uuid.v4();
        const blobCache = tinymce.get(editorId).editorUpload.blobCache;
        const base64 = (reader.result as string)?.split(',')[1];
        const blobInfo = blobCache.create(tempId, file, base64);
        blobCache.add(blobInfo);
        const fileUrl = blobInfo.blobUri();
        const id = await this.uploadMediaBlob(file, followingId, editorId);
        if (editor) {
          const selectedNode = editor.selection.getNode();
          if (
            selectedNode.textContent ||
            selectedNode.nodeName.toLocaleLowerCase() !== 'p' ||
            selectedNode.querySelector('img') ||
            selectedNode.querySelector('video')
          ) {
            if (selectedNode.querySelector('video')) {
              selectedNode.insertAdjacentHTML('afterend', '<p><br></p>');
              tinymce.get(editorId).selection.setCursorLocation(selectedNode.nextElementSibling, 0);
              tinymce.get(editorId).focus();
            }
            this.moveSelectionToEndOfLine(editor);
            editor.insertContent('<p><br></p>');
          }
          if (fileType === 'image') {
            editor.insertContent(`<img src="${fileUrl}" alt="${file.name}" id="${id}">`);
          } else {
            const source = `<source alt="${file.name}" title="${file.name}" src="${fileUrl}">`;
            const videoElement = ` <video controls="controls" width="300" height="150" title="${file.name}" id="${id}">
              ${source}
              </video>`;

            setTimeout(() => {
              editor.insertContent(videoElement);
            }, 100);
          }
        }
        if (callback) {
          callback(fileUrl, {
            alt: file.name,
            //HACK - Assign the blob id to poster param for video elements so that the ID can be extracted when saving content
            poster: id,
          });
        }
      };
    } catch (e) {
      showEditorToasterError(this.showToasterService, editorId);
    }
    reader.readAsDataURL(file);
  }

  private moveSelectionToEndOfLine(editor: Editor) {
    editor.selection.select(editor.selection.getNode(), true);
    editor.selection.collapse(false);
  }

  private async uploadMediaBlob(file: File, followingId: string, editorId: string): Promise<string> {
    const { name, type, size } = file;
    const id = await this.frameEditorBlobService.createBlob(followingId, editorId, name, type);
    if (!id) {
      return;
    }
    const attachment = this.frameEditorBlobService.createdAttachment(id, type, name, size, 'Media');
    this.frameEditorStorageAttachmentsService.addAttachment(followingId, attachment);
    this.blobsService.upload(file, id).catch(() => {
      this.frameEditorBlobService.failedToUpload(editorId, file.name, 'upload_media_failed', id);
    });
    return id;
  }

  validateMediaFileSize(fileSize: number, editorId: string) {
    return validateFileSize(editorId, fileSize, this.MAX_MEDIA_FILE_SIZE_MB, this.showToasterService);
  }

  isSupportMediaUploadFile(type: string): SupportFileType {
    for (const [name, values] of Object.entries(this.ACCEPT_MEDIA_UPLOAD_FILE_TYPES)) {
      if (values.some((v) => type.startsWith(v))) {
        return name as SupportFileType;
      }
    }
  }
}
